import React, { Suspense, useEffect } from 'react';
import './index.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AnimatePresence } from 'framer-motion';

import Navbar from './components/layout/Navbar';
import MobileNav from './components/layout/MobileNav';
import Footer from './components/layout/Footer';
import { useThemeStore } from './store/themeStore';
import TrainDetailsPage from "./pages/TrainDetailsPage";
import StationDetailsPage from './pages/StationDetailsPage';   // ← Import this
import ConnectingTrainDetailsPage from './pages/ConnectingTrainDetailsPage';

// ── Lazy-loaded Pages ─────────────────────────────────────────────────────
const HomePage = React.lazy(() => import('./pages/HomePage'));
const SearchResultsPage = React.lazy(() => import('./pages/SearchResultsPage'));
const PnrPage = React.lazy(() => import('./pages/PnrPage'));
const LiveTrackingPage = React.lazy(() => import('./pages/LiveTrackingPage'));
const AuthPage = React.lazy(() => import('./pages/AuthPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const TrainsPage = React.lazy(() => import('./pages/TrainsPage'));
const StationsPage = React.lazy(() => import('./pages/StationsPage'));
const SchedulesPage = React.lazy(() => import('./pages/SchedulesPage'));
const CommunityPage = React.lazy(() => import('./pages/CommunityPage'));
const ForumPage = React.lazy(() => import('./pages/ForumPage'));
const ChainsPage = React.lazy(() => import('./pages/ChainsPage'));
const SuperSearchPage = React.lazy(() => import('./pages/SuperSearchPage'));
const TravelTipsPage = React.lazy(() => import('./pages/TravelTipsPage'));
const GalleryPage = React.lazy(() => import('./pages/GalleryPage'));
const NewsPage = React.lazy(() => import('./pages/NewsPage'));
const QuizPage = React.lazy(() => import('./pages/QuizPage'));
const FareCalculatorPage = React.lazy(() => import('./pages/FareCalculatorPage'));
const ECateringPage = React.lazy(() => import('./pages/ECateringPage'));
const CancelledTrainsPage = React.lazy(() => import('./pages/CancelledTrainsPage'));
const RetiringRoomsPage = React.lazy(() => import('./pages/RetiringRoomsPage'));
const CoachLayoutPage = React.lazy(() => import('./pages/CoachLayoutPage'));

// ── Query Client ──────────────────────────────────────────────────────────
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

// ── Page Loading Fallback ─────────────────────────────────────────────────
const PageFallback: React.FC = () => (
  <div
    style={{
      minHeight: '60dvh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'var(--text-muted)',
      flexDirection: 'column',
      gap: '16px',
    }}
    role="status"
    aria-label="Loading page"
  >
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        border: '3px solid var(--border-color)',
        borderTopColor: 'var(--brand)',
        animation: 'spin 0.7s linear infinite',
      }}
    />
  </div>
);

// ── Theme Initializer ─────────────────────────────────────────────────────
const ThemeInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { theme } = useThemeStore();
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);
  // Also ensure body background is set immediately
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme || 'dark');
  }, []);
  return <>{children}</>;
};

// ── App ───────────────────────────────────────────────────────────────────
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeInitializer>
          {/* Toast notifications */}
          <Toaster
            position="top-right"
            gutter={8}
            containerStyle={{ top: 72 }}
            toastOptions={{
              duration: 4000,
              style: {
                background: 'var(--bg-elevated)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--radius-xl)',
                fontSize: '0.875rem',
                fontFamily: 'var(--font-family)',
                boxShadow: 'var(--glass-shadow)',
              },
              success: {
                iconTheme: { primary: '#10b981', secondary: 'var(--bg-elevated)' },
              },
              error: {
                iconTheme: { primary: '#ef4444', secondary: 'var(--bg-elevated)' },
              },
            }}
          />

          {/* Layout */}
          <Navbar />

          <Suspense fallback={<PageFallback />}>
            <AnimatePresence mode="wait">
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/search" element={<SearchResultsPage />} />
                <Route path="/trains" element={<TrainsPage />} />
                <Route path="/stations" element={<StationsPage />} />
                <Route path="/schedules" element={<SchedulesPage />} />
                <Route path="/community" element={<CommunityPage />} />
                <Route path="/chains" element={<ChainsPage />} />
                <Route path="/pnr" element={<PnrPage />} />
                <Route path="/forum" element={<ForumPage />} />
                <Route path="/gallery" element={<GalleryPage />} />
                <Route path="/news" element={<NewsPage />} />
                <Route path="/quiz" element={<QuizPage />} />
                <Route path="/super-search" element={<SuperSearchPage />} />
                <Route path="/tips" element={<TravelTipsPage />} />
                <Route path="/fare-calculator" element={<FareCalculatorPage />} />
                <Route path="/ecatering" element={<ECateringPage />} />
                <Route path="/cancelled" element={<CancelledTrainsPage />} />
                <Route path="/retiring-rooms" element={<RetiringRoomsPage />} />
                <Route path="/coach-layout" element={<CoachLayoutPage />} />
                <Route path="/live" element={<LiveTrackingPage />} />
                <Route path="/auth/login" element={<AuthPage />} />
                <Route path="/auth/register" element={<AuthPage />} />
                <Route path="/trains/:trainNumber" element={<TrainDetailsPage />} />
                <Route path="/stations/:stationCode" element={<StationDetailsPage />} />
                <Route path="/connecting-trains/:trainId" element={<ConnectingTrainDetailsPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                {/* 404 */}
                <Route
                  path="*"
                  element={
                    <div
                      style={{
                        minHeight: '60dvh',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '16px',
                        color: 'var(--text-muted)',
                        textAlign: 'center',
                        padding: '2rem',
                      }}
                    >
                      <span style={{ fontSize: '3.5rem' }}>🚂</span>
                      <h1 style={{
                        background: 'linear-gradient(135deg, var(--brand) 0%, var(--accent-purple) 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontSize: '2rem',
                        fontWeight: 800,
                        letterSpacing: '-0.02em',
                      }}>
                        Page Not Found
                      </h1>
                      <p style={{ fontSize: '15px', maxWidth: '320px', lineHeight: 1.5 }}>
                        The route you're looking for doesn't exist or has been moved.
                      </p>
                      <a
                        href="/"
                        style={{
                          color: '#fff',
                          fontWeight: 600,
                          fontSize: '15px',
                          background: 'var(--brand)',
                          padding: '12px 28px',
                          borderRadius: 'var(--radius-md)',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        Go Home
                      </a>
                    </div>
                  }
                />
              </Routes>
            </AnimatePresence>
          </Suspense>

          <Footer />
          <MobileNav />
        </ThemeInitializer>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
